import React, { useState, useMemo, useCallback } from "react";
import {
    ShieldCheck, Search, History, Download, AlertTriangle, Database, X, Loader2, Building2, ChevronDown, ChevronRight, RefreshCw,
} from "lucide-react";
import { useAuditEvents, filterEvents, downloadCsv, actorName, relTime, fullTime, inPeriod, ACTOR_KIND, PERIODS } from "../audit/auditData";

// Global Audit (super-admin, admin): every action in every tenant — who did what, when,
// what changed. Server-written and append-only (backend services/audit.js); older
// activityLogs entries are shown alongside.

const SEV = {
    high: { dot: "bg-red-500", icon: "bg-red-50 text-red-600", label: "High" },
    medium: { dot: "bg-amber-500", icon: "bg-amber-50 text-amber-600", label: "Medium" },
    low: { dot: "bg-blue-500", icon: "bg-blue-50 text-blue-600", label: "Low" },
};
const KIND_STYLE = {
    "platform-admin": "bg-violet-50 text-violet-700", user: "bg-sky-50 text-sky-700", customer: "bg-emerald-50 text-emerald-700", system: "bg-slate-100 text-slate-600",
};
const select = "px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-700 outline-none focus:border-navy-300";
const initials = (s = "") => s.split(/[\s@.]+/).filter(Boolean).slice(0, 2).map(w => w[0]).join("").toUpperCase() || "?";

export default function GlobalAudit() {
    const { events, tenants, categories, loading, error, reload, loadOlder } = useAuditEvents("/api/audit");
    const [f, setF] = useState({ q: "", period: "30d", tenant: "", category: "", severity: "", kind: "" });
    const [open, setOpen] = useState(null);
    const set = k => v => setF(x => ({ ...x, [k]: v }));
    const tenantName = useCallback(id => tenants[id] || id || "—", [tenants]);

    const shown = useMemo(() => filterEvents(events, f), [events, f]);
    const last30 = useMemo(() => events.filter(e => inPeriod(e, "30d")), [events]);
    const stats = {
        total: last30.length,
        high: last30.filter(e => e.severity === "high").length,
        impersonations: last30.filter(e => /impersonat/i.test(e.action)).length,
        tenants: new Set(last30.map(e => e.tenantId).filter(t => t && t !== "platform")).size,
    };
    const tenantIds = useMemo(() => [...new Set(events.map(e => e.tenantId).filter(Boolean))].sort((a, b) => tenantName(a).localeCompare(tenantName(b))), [events, tenantName]);
    const filtersOn = f.q || f.tenant || f.category || f.severity || f.kind || f.period !== "30d";

    return (
        <div className="max-w-7xl mx-auto py-8 px-4">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
                <div>
                    <h1 className="text-3xl font-bold text-navy-900 font-serif flex items-center gap-3">
                        <ShieldCheck className="text-navy-900" size={32} /> Global Audit Log
                    </h1>
                    <p className="text-slate-500 mt-1">Every action in every tenant — who did what, when, and what changed. Written by the server; it can't be edited.</p>
                </div>
                <div className="flex gap-2">
                    <button onClick={reload} className="px-4 py-2.5 bg-white border border-slate-300 text-navy-900 rounded-xl font-bold flex items-center gap-2 hover:bg-slate-50 shadow-sm">
                        <RefreshCw size={16} className={loading ? "animate-spin" : ""} /> Refresh
                    </button>
                    <button onClick={() => downloadCsv(shown, { tenants, file: "global-audit" })} className="px-5 py-2.5 bg-white border border-slate-300 text-navy-900 rounded-xl font-bold flex items-center gap-2 hover:bg-slate-50 shadow-sm">
                        <Download size={18} /> Export Compliance Report
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <Stat label="Total actions (30d)" value={stats.total} icon={Database} loading={loading} />
                <Stat label="High severity (30d)" value={stats.high} icon={AlertTriangle} tone="red" loading={loading} onClick={() => setF(x => ({ ...x, severity: x.severity === "high" ? "" : "high" }))} active={f.severity === "high"} />
                <Stat label="Impersonation events" value={stats.impersonations} icon={History} tone="blue" loading={loading} onClick={() => setF(x => ({ ...x, q: x.q === "impersonation" ? "" : "impersonation" }))} active={f.q === "impersonation"} />
                <Stat label="Tenants with activity" value={stats.tenants} icon={Building2} tone="violet" loading={loading} />
            </div>

            <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200 mb-6 space-y-3">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                    <input value={f.q} onChange={e => set("q")(e.target.value)} placeholder="Search who, what, record, tenant or a changed value…"
                        className="w-full pl-9 pr-9 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-navy-900/10 focus:border-navy-300" />
                    {f.q && <button onClick={() => set("q")("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"><X size={14} /></button>}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <div className="flex rounded-xl border border-slate-200 p-0.5 bg-slate-50">
                        {PERIODS.map(([id, label]) => (
                            <button key={id} onClick={() => set("period")(id)} className={`px-3 py-1.5 rounded-lg text-xs font-bold ${f.period === id ? "bg-navy-900 text-white" : "text-slate-500 hover:text-slate-700"}`}>{label}</button>
                        ))}
                    </div>
                    <select value={f.tenant} onChange={e => set("tenant")(e.target.value)} className={select}>
                        <option value="">All tenants</option>
                        {tenantIds.map(t => <option key={t} value={t}>{tenantName(t)}</option>)}
                    </select>
                    <select value={f.category} onChange={e => set("category")(e.target.value)} className={select}>
                        <option value="">All areas</option>
                        {Object.entries(categories).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                    <select value={f.severity} onChange={e => set("severity")(e.target.value)} className={select}>
                        <option value="">All severities</option>
                        {Object.entries(SEV).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                    </select>
                    <select value={f.kind} onChange={e => set("kind")(e.target.value)} className={select}>
                        <option value="">Anyone</option>
                        {Object.entries(ACTOR_KIND).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                    </select>
                    {filtersOn && <button onClick={() => setF({ q: "", period: "30d", tenant: "", category: "", severity: "", kind: "" })} className="text-xs font-bold text-slate-400 hover:text-red-500 flex items-center gap-1"><X size={12} /> Clear</button>}
                    <span className="ml-auto text-xs text-slate-400 font-medium">{shown.length} of {events.length} loaded</span>
                </div>
            </div>

            {error && <div className="mb-4 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">{error}</div>}

            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                {loading && !events.length ? (
                    <div className="flex items-center justify-center py-20"><Loader2 size={28} className="animate-spin text-slate-300" /></div>
                ) : !shown.length ? (
                    <div className="flex flex-col items-center justify-center py-20 text-center">
                        <ShieldCheck size={32} className="text-slate-200 mb-3" />
                        <p className="font-bold text-slate-400">No audit events{filtersOn ? " match these filters" : " yet"}</p>
                        <p className="text-sm text-slate-300 mt-1">{filtersOn ? "Try a longer period or clear the filters." : "Actions appear here as people use the platform."}</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead className="text-[10px] font-bold text-slate-400 uppercase tracking-wider bg-slate-50/60 border-b border-slate-100">
                                <tr>
                                    <th className="px-6 py-4">What happened</th>
                                    <th className="px-6 py-4">Who</th>
                                    <th className="px-6 py-4">Tenant</th>
                                    <th className="px-6 py-4">When</th>
                                    <th className="px-6 py-4 text-right">Ref</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                                {shown.map(e => {
                                    const sev = SEV[e.severity] || SEV.low;
                                    const isOpen = open === e.id;
                                    return (
                                        <React.Fragment key={e.id}>
                                            <tr onClick={() => setOpen(isOpen ? null : e.id)} className="hover:bg-slate-50/60 cursor-pointer group align-top">
                                                <td className="px-6 py-4">
                                                    <div className="flex items-start gap-3">
                                                        <div className={`p-2 rounded-lg shrink-0 ${sev.icon}`}><History size={15} /></div>
                                                        <div className="min-w-0">
                                                            <p className="text-sm text-navy-900"><b>{actorName(e)}</b> {e.summary}</p>
                                                            {e.changes?.length > 0 && (
                                                                <div className="flex flex-wrap gap-1.5 mt-1.5">
                                                                    {e.changes.slice(0, 4).map((c, i) => (
                                                                        <span key={i} className="px-2 py-0.5 rounded-md bg-slate-100 text-[11px] text-slate-600">
                                                                            {c.field}: <span className="line-through text-slate-400">{c.from}</span> → <b className="text-navy-900">{c.to}</b>
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            )}
                                                            <p className="text-[11px] text-slate-400 mt-1">{categories[e.category] || e.category}{e.source === "legacy" ? " · older log" : ""}</p>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-2">
                                                        <div className="w-7 h-7 rounded-full bg-navy-900 text-white flex items-center justify-center text-[9px] font-bold shrink-0">{initials(actorName(e))}</div>
                                                        <div className="min-w-0">
                                                            <p className="text-sm font-medium text-navy-900 truncate max-w-[180px]">{actorName(e)}</p>
                                                            <span className={`inline-block mt-0.5 px-1.5 py-px rounded text-[10px] font-bold ${KIND_STYLE[e.actor?.kind] || KIND_STYLE.system}`}>{ACTOR_KIND[e.actor?.kind]?.label || "System"}{e.actor?.role ? ` · ${e.actor.role}` : ""}</span>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4"><span className="px-3 py-1 bg-slate-100 text-slate-700 rounded-lg font-bold text-[10px] uppercase tracking-wide whitespace-nowrap">{tenantName(e.tenantId)}</span></td>
                                                <td className="px-6 py-4 text-xs font-medium text-slate-500 whitespace-nowrap" title={fullTime(e.at)}>{relTime(e.at)}</td>
                                                <td className="px-6 py-4 text-right whitespace-nowrap">
                                                    <span className="text-[10px] font-mono text-slate-300 group-hover:text-slate-500">{e.id.slice(0, 8)}</span>
                                                    {isOpen ? <ChevronDown size={14} className="inline ml-1 text-slate-400" /> : <ChevronRight size={14} className="inline ml-1 text-slate-300" />}
                                                </td>
                                            </tr>
                                            {isOpen && (
                                                <tr className="bg-slate-50/60">
                                                    <td colSpan={5} className="px-6 py-4">
                                                        <EventDetail e={e} tenantName={tenantName} categories={categories} />
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
                {loadOlder && events.length > 0 && (
                    <div className="border-t border-slate-100 p-3 text-center">
                        <button onClick={loadOlder} disabled={loading} className="text-sm font-bold text-navy-900 hover:underline disabled:opacity-50">{loading ? "Loading…" : "Load older events"}</button>
                    </div>
                )}
            </div>
        </div>
    );
}

function Stat({ label, value, icon: Icon, tone, loading, onClick, active }) {
    const t = { red: ["border-l-red-500", "text-red-500", "bg-red-50"], blue: ["border-l-blue-500", "text-blue-500", "bg-blue-50"], violet: ["border-l-violet-500", "text-violet-500", "bg-violet-50"] }[tone] || ["", "text-slate-400", "bg-slate-50"];
    const Comp = onClick ? "button" : "div";
    return (
        <Comp onClick={onClick} className={`text-left bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex items-center justify-between ${tone ? `border-l-4 ${t[0]}` : ""} ${active ? "ring-2 ring-navy-900/20" : ""}`}>
            <div>
                <div className="text-3xl font-bold text-navy-900 font-serif">{loading ? <Loader2 size={22} className="animate-spin text-slate-300" /> : value.toLocaleString()}</div>
                <div className={`text-[11px] font-bold uppercase tracking-widest mt-1 ${t[1]}`}>{label}</div>
            </div>
            <div className={`p-3 rounded-xl ${t[2]} ${t[1]}`}><Icon size={22} /></div>
        </Comp>
    );
}

export function EventDetail({ e, tenantName, categories }) {
    const rows = [
        ["When", fullTime(e.at)], ["Tenant", tenantName(e.tenantId)], ["Who", `${actorName(e)}${e.actor?.email && e.actor.email !== actorName(e) ? ` <${e.actor.email}>` : ""}`],
        ["Role", [ACTOR_KIND[e.actor?.kind]?.label, e.actor?.role].filter(Boolean).join(" · ") || "—"], ["Area", categories[e.category] || e.category || "—"],
        ["Action code", e.action], ["Record", e.entity ? `${e.entity.type}${e.entity.label ? ` · ${e.entity.label}` : ""}${e.entity.id && e.entity.id !== e.entity.label ? ` (${e.entity.id})` : ""}` : "—"],
        ["From", e.ip ? `${e.ip}${e.userAgent ? ` · ${e.userAgent}` : ""}` : "—"], ["Event id", e.id],
    ];
    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 text-sm">
            <dl className="grid grid-cols-[110px_1fr] gap-x-3 gap-y-1.5">
                {rows.map(([k, v]) => <React.Fragment key={k}><dt className="text-slate-400 text-xs font-bold uppercase tracking-wide pt-0.5">{k}</dt><dd className="text-navy-900 break-all">{v}</dd></React.Fragment>)}
            </dl>
            {e.changes?.length > 0 && (
                <div>
                    <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-1.5">Changes</p>
                    <table className="w-full text-xs bg-white rounded-lg border border-slate-200 overflow-hidden">
                        <thead className="bg-slate-50 text-slate-400"><tr><th className="px-3 py-1.5 text-left">Field</th><th className="px-3 py-1.5 text-left">Before</th><th className="px-3 py-1.5 text-left">After</th></tr></thead>
                        <tbody className="divide-y divide-slate-100">
                            {e.changes.map((c, i) => <tr key={i}><td className="px-3 py-1.5 font-semibold text-navy-900">{c.field}</td><td className="px-3 py-1.5 text-slate-500">{c.from}</td><td className="px-3 py-1.5 text-navy-900">{c.to}</td></tr>)}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

